import React from 'react';
import { Modal, Text, View } from 'react-native';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';
import { Button } from '../../Button';

export interface DecisionAction {
  label: string;
  onPress: () => void | Promise<void>;
  /** `warning` for the destructive choice, `active` for the primary one; omitted for a plain dismissal. */
  state?: 'warning' | 'active';
  disabled?: boolean;
  loading?: boolean;
  testID?: string;
}

export interface DecisionDialogProps {
  visible: boolean;
  title: string;
  message?: string;
  /** Stacked top to bottom; put the dismissal last. */
  actions: DecisionAction[];
  onRequestClose: () => void;
  testID?: string;
}

/**
 * A modal sheet that asks one question and offers any number of stacked
 * choices; `ConfirmationOverlay` is the same sheet limited to two.
 */
export const DecisionDialog: React.FC<DecisionDialogProps> = ({
  visible,
  title,
  message,
  actions,
  onRequestClose,
  testID,
}) => (
  <Modal
    visible={visible}
    transparent
    animationType="fade"
    onRequestClose={onRequestClose}
  >
    <View style={styles.overlay} testID={testID}>
      <View style={styles.sheet}>
        <Text style={styles.title}>{title}</Text>
        {message ? <Text style={styles.message}>{message}</Text> : null}
        {actions.map((action) => (
          <Button
            key={action.label}
            variant="rectangular"
            state={action.state}
            style={styles.actionButton}
            onPress={action.onPress}
            disabled={action.disabled}
            loading={action.loading}
            size="large"
            testID={action.testID}
          >
            {action.label}
          </Button>
        ))}
      </View>
    </View>
  </Modal>
);

DecisionDialog.displayName = 'DecisionDialog';

const styles = themedStyleSheet((role) => ({
  overlay: {
    flex: 1,
    backgroundColor: role.scrim,
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: role.surface,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
    color: role.textStrong,
    marginBottom: 6,
    textAlign: 'center',
  },
  message: {
    fontSize: 14,
    color: role.textSecondary,
    textAlign: 'center',
    marginBottom: 20,
  },
  actionButton: {
    marginBottom: 8,
    width: '100%',
  },
}));
